import { useAuthentication } from '@/services/AuthService';
import { useState } from 'react';
import { Palette } from './Palette';
import {
  ErrorMessage,
  Font,
  Input,
  PrimaryButton,
  Row,
  SecondaryButton,
  SuccessMessage,
  ToggleSwitch,
} from './DesignSystem';

export type AuthFormMode = 'register' | 'login' | 'waitlist';

export function LogoutButton() {
  const { dispatch } = useAuthentication();
  return (
    <SecondaryButton
      text="Logout"
      onClick={() => dispatch({ type: 'log-out' })}
    />
  );
}

export function AuthForm({
  mode,
  onChangeMode,
}: {
  mode: AuthFormMode;
  onChangeMode: (mode: AuthFormMode) => void;
}) {
  const { state, dispatch } = useAuthentication();

  if (state.type === 'authenticated') {
    return <></>;
  }

  const modeToText = {
    ['register']: 'Register',
    ['login']: 'Login',
    ['waitlist']: 'Join the Waitlist',
  };

  const handleModeChange = (nextMode: AuthFormMode) => {
    dispatch({ type: 'clear-error' });
    onChangeMode(nextMode);
  };

  return (
    <Palette title={modeToText[mode]} windowID="auth">
      {mode === 'waitlist' && <WaitlistForm />}
      {mode === 'register' && (
        <RegisterForm onClickLogin={() => handleModeChange('login')} />
      )}
      {mode === 'login' && (
        <LoginForm onClickRegister={() => handleModeChange('register')} />
      )}
    </Palette>
  );
}

function LoginForm({ onClickRegister }: { onClickRegister: () => void }) {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const { state, service } = useAuthentication();
  const isEmailEmpty = email.trim().length === 0;
  const isPasswordEmpty = password.trim().length === 0;

  const submit = () => {
    if (state.isLoading) {
      return;
    }

    service.login({ email, password });
  };

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column' }}
      onSubmit={(e) => {
        submit();
        e.preventDefault();
      }}
    >
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={setEmail}
        isDisabled={state.isLoading}
      />
      <Input
        type="password"
        placeholder="Enter your password"
        value={password}
        onChange={setPassword}
        isDisabled={state.isLoading}
      />
      <Row
        style={{
          marginBlockStart: 8,
          paddingBlock: 8,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SecondaryButton
          text="Register"
          onClick={onClickRegister}
          isDisabled={state.isLoading}
        />
        <Font kind="font-caption" style={{ color: '#676767d0' }}>
          or
        </Font>
        <PrimaryButton
          text="Login"
          isLoading={state.isLoading}
          isDisabled={isEmailEmpty || isPasswordEmpty}
          onClick={submit}
        />
      </Row>
      <AuthErrorMessage message={state.errorMessage} />
    </form>
  );
}

function WaitlistForm() {
  const { state, service } = useAuthentication();
  const [email, setEmail] = useState<string>('');
  const isEmailEmpty = email.trim().length === 0;

  function getErrorMessage(): string | null {
    if (state.errorMessage !== null) {
      return state.errorMessage;
    }

    return null;
  }

  const submit = () => {
    if (state.isLoading) {
      return;
    }

    service.joinWaitlist({ email });
  };

  const hasJoinedWaitlist =
    state.type === 'unauthenticated' && state.hasJoinedWaitlist === true;

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column' }}
      onSubmit={(e) => {
        submit();
        e.preventDefault();
      }}
    >
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={setEmail}
        isDisabled={state.isLoading}
      />
      <PrimaryButton
        text="Join the Waitlist"
        isLoading={state.isLoading}
        isDisabled={isEmailEmpty}
        onClick={submit}
      />
      <AuthErrorMessage message={getErrorMessage()} />
      {hasJoinedWaitlist && <SuccessMessage message="You're on the list!" />}
    </form>
  );
}

function RegisterForm({ onClickLogin }: { onClickLogin: () => void }) {
  const { state, service } = useAuthentication();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const passwordTooShort = password.length < 6;
  const [passwordInputHasLostFocus, setPasswordInputHasLostFocus] =
    useState<boolean>(false);
  const isEmailEmpty = email.trim().length === 0;
  const isPasswordEmpty = password.trim().length === 0;

  function getErrorMessage(): string | null {
    if (state.errorMessage !== null) {
      return state.errorMessage;
    }

    if (passwordTooShort && passwordInputHasLostFocus) {
      // To avoid showing the error message while the user is typing, we
      // wait until the password input has lost focus at least once.
      return 'Passwords must be at least 6 characters';
    }

    return null;
  }

  const submit = () => {
    if (state.isLoading) {
      return;
    }

    service.register({ email, password });
  };

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column' }}
      onSubmit={(e) => {
        submit();
        e.preventDefault();
      }}
    >
      <Input
        type="email"
        placeholder="Enter an email address"
        value={email}
        onChange={setEmail}
        isDisabled={state.isLoading}
      />
      <Input
        type="password"
        placeholder="Enter a password"
        value={password}
        onChange={setPassword}
        isDisabled={state.isLoading}
        onBlur={() => setPasswordInputHasLostFocus(true)}
      />
      <Row
        style={{
          marginBlockStart: 8,
          paddingBlock: 8,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SecondaryButton
          text="Login"
          onClick={onClickLogin}
          isDisabled={state.isLoading}
        />
        <Font kind="font-caption" style={{ color: '#676767d0' }}>
          or
        </Font>
        <PrimaryButton
          text="Register"
          isLoading={state.isLoading}
          isDisabled={isEmailEmpty || isPasswordEmpty}
          onClick={submit}
        />
      </Row>
      <AuthErrorMessage message={getErrorMessage()} />
    </form>
  );
}

function AuthErrorMessage({ message }: { message?: string | null }) {
  if (message === undefined || message === null) {
    return null;
  }

  return (
    <div
      role="alert"
      style={{
        marginBlock: 8,
        marginInline: 8,
        textAlign: 'center',
      }}
    >
      <ErrorMessage message={message} />
    </div>
  );
}
